import { setTypography, setVh } from "@/mono/lib/styles/mixins";

import { forwardRef, memo, useImperativeHandle, useRef } from "react";

import { events } from "@/common/constants";
import ShuffledText from "@/mono/components/ShuffledText";
import { TransitionLink } from "@/mono/components/TransitionLink";
import emitter from "@/mono/lib/emitter";
import useAnimation from "@/mono/lib/hooks/useAnimation";
import useEvent from "@/mono/lib/hooks/useEvent";
import { useIsClient } from "@/mono/lib/hooks/useIsClient";
import { mediaMobile } from "@/mono/lib/responsive";
import Link from "next/link";
import styled, { css } from "styled-components";
import { create } from "zustand";
import AnimatedHoverText from "../AnimatedHoverText";
import BorderedIcon from "../BorderedIcon";
import useResize from "@/mono/lib/hooks/useResize";
import { useGlobalStore } from "@/mono/store/global";
import { maxWidth } from "@/common/theme";
import Workers from "mono/lib/webworkers/Workers";
import { mediaTabletDown } from "mono/lib/responsive";

export const menuStore = create((set) => ({
  isOpen: false,
}));

const MenuItem = forwardRef(({ getIsVisible, href, text, i }, ref) => {
  const hoverText = useRef();

  useEvent(events.ROUTE_CHANGE_START, () => {
    hoverText.current.animateOut();

    if (hoverText.current.isHoverVisible()) {
      hoverText.current.triggerLeave();
    }
  });

  useImperativeHandle(ref, () => ({
    animateIn: () => {
      hoverText.current?.animateIn({
        delay: 0.1 * i,
        duration: 0.5,

        onComplete: () => {
          hoverText.current?.toggleEnabled(true);
        },
      });
    },
    animateOut: () => {
      hoverText.current?.animateOut({
        duration: 0.05,
        delay: 0,
        onStart: () => {
          hoverText.current?.toggleEnabled(false);
        },
      });
    },
  }));

  return (
    <TransitionLink href={href}>
      <AnimatedHoverText
        ref={hoverText}
        enabled={false}
        className="menu-link-wrapper"
        secondaryTextClassName="menu-link-bold"
      >
        {text}
      </AnimatedHoverText>
    </TransitionLink>
  );
});

export default memo(function Menu() {
  const wrapper = useRef();
  const menuLinks = useRef();
  const items = useRef([]);
  const isVisible = useRef(false);
  const closeIcon = useRef();

  /**
   * Links animation in/out
   */
  const { animateIn, animateOut: animateLinksOut } = useAnimation({
    inParams: {
      delay: 0.6,
      onStart: () => {
        closeIcon.current?.animateIn({ delay: 0.4 });
        items.current?.forEach((link, i) => {
          if (!link) return;

          link.animateIn({ duration: 0.4, delay: 0.1 * i });
        });
      },
      onComplete: () => {
        isVisible.current = true;
      },
    },
    outParams: {
      onStart: () => {
        isVisible.current = false;
        items.current.forEach((link) => {
          if (!link) return;

          link.animateOut({ duration: 0.1 });
        });

        closeIcon.current?.animateOut();
      },
      onComplete: () => {
        isVisible.current = false;
      },
    },
  });

  function animateOut() {
    menuLinks.current.classList.add("inactive");
    animateLinksOut();
    if (!menuStore.getState().isOpen) Workers.animateGLBackgroundOut();
  }

  useEvent(events.MENU_OPEN, () => {
    menuStore.setState({ isOpen: true });
    Workers.animateGLBackgroundIn();

    // useGlobalStore.getState().lenis.stop();
    if (wrapper.current) wrapper.current.style.display = "flex";
    menuLinks.current.classList.remove("inactive");
    animateIn();
  });

  function hide() {
    menuStore.setState({ isOpen: false });
    if (wrapper.current) wrapper.current.style.display = "none";
  }

  useEvent(events.MENU_CLOSE, () => {
    menuStore.setState({ isOpen: false });
    // useGlobalStore.getState().lenis.start();
    animateOut();
  });
  useEvent(events.MENU_OUT_COMPLETE, hide);
  useEvent(events.ROUTE_CHANGE_START, () => {
    animateOut();
  });

  const getIsVisible = () => isVisible.current;

  useResize(
    () => {
      const windowWidth = useGlobalStore.getState().windowSize.width;
      const multiplier = windowWidth < maxWidth ? 8 : 2;

      if (wrapper.current)
        wrapper.current.style.setProperty("--menuWidthMultiplier", multiplier);
    },
    { immediate: true },
    [wrapper],
  );

  const isClient = useIsClient();
  if (!isClient) return null;

  // FIXME: Will this create a new canvas every time?
  return (
    <Wrapper ref={wrapper}>
      <Inner>
        <div className="menu__links" ref={menuLinks}>
          <MenuItem
            getIsVisible={getIsVisible}
            ref={(r) => (items.current[0] = r)}
            i={0}
            href="/"
            text="Home"
          />
          <MenuItem
            getIsVisible={getIsVisible}
            ref={(r) => (items.current[1] = r)}
            i={1}
            href="/about-us"
            text="About us"
          />
          <MenuItem
            getIsVisible={getIsVisible}
            ref={(r) => (items.current[2] = r)}
            i={2}
            href="/projects"
            text="Projects"
          />
          <MenuItem
            getIsVisible={getIsVisible}
            ref={(r) => (items.current[3] = r)}
            i={3}
            href="/what-we-do"
            text="What we do"
          />
          {/* <MenuItem getIsVisible={getIsVisible}
            ref={(r) => (items.current[4] = r)}
            href="/contact"b
            text="Contact"
          /> */}
        </div>
        <div className="menu__bottom">
          <div className="menu__contact">
            <Link href="tel:+31765156463">
              <ShuffledText
                ref={(r) => (items.current[4] = r)}
                text="+31765156463"
                //animateOnScroll={false}
              ></ShuffledText>
            </Link>
            <Link href="mailto:cu@nine.nl">
              <ShuffledText
                ref={(r) => (items.current[5] = r)}
                //animateOnScroll={false}
                text="cu@nine.nl"
              ></ShuffledText>
            </Link>
          </div>
          <div className="menu__socials">
            <Link href="https://www.linkedin.com/company/nine-nl/">
              <ShuffledText
                ref={(r) => (items.current[7] = r)}
                text="LinkedIn"
              ></ShuffledText>
            </Link>
            <Link href="https://www.instagram.com/ninecreativeagency/">
              <ShuffledText
                ref={(r) => (items.current[6] = r)}
                text="Instagram"
              ></ShuffledText>
            </Link>

            {/* <Link href="https://vimeo.com/nine">
              <ShuffledText
                ref={(r) => (items.current[8] = r)}
                text="Vimeo"
              ></ShuffledText>
            </Link> */}
          </div>
        </div>

        <BorderedIcon
          icon="close"
          ref={closeIcon}
          onClick={() => emitter.emit(events.MENU_CLOSE)}
          animateOnScroll={false}
        />
      </Inner>
    </Wrapper>
  );
});

const Wrapper = styled.div`
  --menuWidthMultiplier: ${(props) =>
    props.theme.util.isUnderMaxWidth() ? 8 : 2};

  position: fixed;

  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 7;
  width: 100vw;

  display: none;

  height: calc(${setVh(100)});

  canvas {
    width: 100%;
    height: 100%;
    pointer-events: none;
    position: absolute;
  }
`;

const Inner = styled.div`
  position: absolute;

  z-index: 5;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 320rem;

  .icon-container {
    position: absolute;

    top: 50rem;
    right: 0;

    .icon-wrapper {
      color: ${({ theme }) => theme.theme.menuText} !important;
      border-color: ${({ theme }) => theme.theme.menuText} !important;
    }
    .icon-background {
      background-color: ${({ theme }) => theme.theme.menuText} !important;
    }

    path {
      fill: ${({ theme }) => theme.theme.menuText} !important;
      stroke: ${({ theme }) => theme.theme.menuText} !important;
    }

    .icon-duplicate {
      fill: ${({ theme }) => theme.theme.menuBackground} !important;

      path {
        fill: ${({ theme }) => theme.theme.menuBackground} !important;
        stroke: ${({ theme }) => theme.theme.menuBackground} !important;
      }
    }
  }

  * {
    color: ${({ theme }) => theme.theme.menuText} !important;
  }

  .menu__links {
    display: flex;
    flex-direction: column;
    a {
      // HINT: Override next link for hover effect
      pointer-events: all !important;
      cursor: pointer !important;
      display: inline-block;
    }

    &.inactive {
      a {
        pointer-events: none !important;
        cursor: default !important;
        user-select: none !important;
      }
    }

    h2 {
      font-weight: 100;
      white-space: nowrap;
      margin-bottom: 40rem;
      ${setTypography("menuLinks")};
    }
  }

  .menu__bottom {
    width: 100%;
    display: flex;

    justify-content: space-between;

    .txt-shuffle {
      ${setTypography("menuMono")};
      /* color: ${({ theme }) => theme.theme.accent}; */
    }
    .menu__contact {
      display: flex;
      gap: 150rem;
    }

    .menu__socials {
      display: flex;
      gap: 150rem;
    }
  }

  ${mediaTabletDown(css`
    width: calc(100% - ${(props) => props.theme.misc.pagePadding()} * 4);

    gap: 150rem;
    margin-bottom: 400rem !important;

    .icon-container {
      position: fixed;
      top: calc(${(props) => props.theme.misc.pagePadding()} * 2);
      right: calc(${(props) => props.theme.misc.pagePadding()} * 2);
    }

    .menu__bottom,
    .menu__contact {
      flex-direction: column;
    }

    .menu__contact {
      gap: 70rem !important;
    }

    .menu__bottom {
      justify-content: space-between;

      * {
        /* font-family: "Restart Soft", sans-serif !important;
        text-transform: none !important;
        font-weight: 100 !important; */
      }
    }

    .menu__socials {
      position: fixed;
      bottom: calc(${(props) => props.theme.misc.pagePadding()} * 3);
      gap: 100rem !important;
    }
  `)}

  width: calc(
    100% - ${(props) =>
    props.theme.misc.pagePadding()} * var(--menuWidthMultiplier)
  );
`;
